import * as React from 'react';
import styled from 'styled-components';

import { useParams } from 'react-router';
import { animated, useSpring } from 'react-spring';
import { useInterval } from '../../../utils/hooks/useInterval';
import { useTimeout } from '../../../utils/hooks/useTimeOut';
import { H1 } from '../HeaderTitle';
import { CarouselButtonRight } from '../../ui/button/CarouselButtonRight';
import { CarouselButtonLeft } from '../../ui/button/CarouselButtonLeft';

//Image
import { Image } from '../../ui/broker/Image';
import { CoverImage } from '../CoverImage';
import { useEffect } from 'react';
import { AssignmentType, Estate, SearchEstateSort, useEstateCarouselQuery } from '../../../__types__/generated/cluster';
import { env } from '../../../__config__/env';
import { client } from '../../../../client/__graphql__/clusterClient';

const Header = styled.div``;

interface Image {
  id: string | number;
  loaded: boolean;
  url: string;
  description?: string;
  location?: string;
  property_id: number;
  linkTo?: string;
}

export const ImageCarousel: React.FC<{
  title?: string | null;
  url?: string | null;
  imageList?: any | null;
  get_properties?: boolean | null;
}> = ({ title, url, imageList, get_properties }) => {
  const params = useParams<{ urltag: string }>();
  const [startInterval, setStartInterval] = React.useState(false);
  const [clearInterval, setClearInterval] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [loaded, SetLoaded] = React.useState(false);
  const [property_id, SetPropertyId] = React.useState(0);
  // Laster default bilde
  const [images, setImages] = React.useState<Image[]>([]);
  const [use_carousel, SetUseCarousel] = React.useState(imageList && imageList?.length > 0);
  const [preload, SetPreload] = React.useState(false);
  const [coverUrl, SetCoverUrl] = React.useState(url);

  const lazyLoad = (index: number) => {
    setImages((prev) => {
      return prev.map((item, imageIndex) => {
        if (index === imageIndex || index + 1 === imageIndex) {
          return { ...item, loaded: true };
        } else {
          return item;
        }
      });
    });
  };

  if (!preload) {
    SetPreload(true);

    if (imageList && imageList?.length > 0) {
      if (imageList.length > 1 || get_properties) {
        imageList.map((item: any, index: any) => {
          setImages((prev: any) => [
            ...prev,
            {
              loaded: false,
              id: `img_${index}`,
              url: item.url,
              description: item.description
            }
          ]);
        });

        SetUseCarousel(true);
        lazyLoad(0);
      } else if (imageList[0].url) {
        SetCoverUrl(imageList[0].url);
        SetUseCarousel(false);
      }
    }

    if (url && url.length > 0 && get_properties == true && !use_carousel && images.length < 1) {
      setImages((prev: any) => [
        ...prev,
        {
          loaded: false,
          id: 'img_0',
          url: url,
          description: ''
        }
      ]);

      SetUseCarousel(true);
      lazyLoad(0);
    }
  }

  const { data, loading } = useEstateCarouselQuery({
    skip: !(get_properties === true && params.urltag && params.urltag.length > 0),
    client: client,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        brandId: env.BRAND_ID
      },
      filter: {
        sort: SearchEstateSort.PublishedNewOld,
        assignmentTypes: [AssignmentType.NewDevelopment, AssignmentType.NewDevelopmentCommercial, AssignmentType.NewDevelopmentLeisure],
        tags: [params?.urltag],
        sold: false,
        isProjectUnit: false,
        published: true,
        available: true,
        pagination: {
          limit: 4
        }
      }
    }
  });

  useEffect(() => {
    if (data?.search?.estate?.list) {
      data?.search?.estate.list.map((item: any, index: number) => {
        setImages((prev: any) => [
          ...prev,
          {
            loaded: false,
            id: `img_${images.length + index}`,
            url: item?.images?.main?.url,
            location: item.postalPlace,
            description: item.description,
            property_id: item.assignmentNumber
          }
        ]);
      });
    }
  }, [data]);

  const OnImageClick = () => {
    if (property_id > 0) {
      window.open(`https://privatmegleren.no/` + property_id, '_blank');
    }
  };

  const UpdatePropertyId = (index: number) => {
    if (index > -1 && index < images.length && images[index] && images[index]?.property_id > 0) {
      SetPropertyId(images[index].property_id);
    } else if (property_id != 0) {
      SetPropertyId(0);
    }
  };

  const getNextImage = (index: number, images: any[]) => {
    // Hvis enden av tabell med indekser, gå tilbake til første indeks(0)
    if (images.length - 1 === index) {
      return 0;
    }
    // ellers gå en indeks videre
    return index + 1;
  };

  const getPrevImage = (index: number, images: any[]) => {
    // Hvis på starten av tabellen
    if (index === 0) {
      // Gå tilbake til siste indeks av tabellen
      return images.length - 1;
    }
    // Eller returner forrige index
    return index - 1;
  };

  useTimeout(() => {
    setStartInterval(true);
  }, 10000);

  useInterval({
    cb: () => {
      if (startInterval) {
        const imageIndex = getNextImage(currentImageIndex, images);
        setCurrentImageIndex(imageIndex);
        UpdatePropertyId(imageIndex);
        lazyLoad(imageIndex);
      }
    },
    delay: clearInterval ? null : 5500
  });

  React.useEffect(() => {
    if (clearInterval) {
      setClearInterval(false);
    }
  }, [clearInterval]);

  if (!use_carousel) {
    return <CoverImage url={coverUrl} />;
  }

  return (
    <StyledHeaderTop>
      {images.map((item, index) => {
        return (
          <Slide key={index} className={index === currentImageIndex ? 'show-slide' : ''}>
            <img data-src={item.url} src={item.loaded ? item.url : ''} alt="" />
          </Slide>
        );
      })}
      <HeaderTopOverlay onClick={OnImageClick} style={{ cursor: property_id > 0 ? 'pointer' : 'auto' }} />
      <CarouselButtonLeft
        onClick={() => {
          const imageIndex = getPrevImage(currentImageIndex, images);
          setCurrentImageIndex(imageIndex);
          UpdatePropertyId(imageIndex);
          //console.log(imageIndex);
          lazyLoad(imageIndex);
          setClearInterval(true);
        }}
      />

      <StyledHeaderContent>
        {images.map((item, index) => {
          if (index === currentImageIndex) {
            if (item?.description) {
              return <HeaderContent key={index} description={item.description} location={item.location} object_id={item.property_id} />;
            }
            return <HeaderContent key={index} description={title ? title : ''} />;
          }
        })}
      </StyledHeaderContent>

      <CarouselButtonRight
        onClick={() => {
          const imageIndex = getNextImage(currentImageIndex, images);
          setCurrentImageIndex(imageIndex);
          UpdatePropertyId(imageIndex);
          lazyLoad(imageIndex);
          setClearInterval(true);
        }}
      />
    </StyledHeaderTop>
  );
};

const ContainerClick = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const HeaderContent: React.FC<{
  description: string;
  location?: string;
  object_id?: any;
}> = ({ description, location, object_id }) => {
  const animatedProps = useSpring({
    delay: 500,
    from: { opacity: 0, transform: 'translate3d(0, -20px, 0)' },
    to: { opacity: 1, transform: 'translate3d(0, 0px, 0)' }
  });

  if (object_id) {
    return (
      <PropertyUrl target="_blank" href={'https://www.privatmegleren.no/' + object_id}>
        <animated.div style={animatedProps}>
          {location ? <p>{location}</p> : null}
          <H1Title className="property" dangerouslySetInnerHTML={{ __html: description }} />
        </animated.div>
      </PropertyUrl>
    );
  }

  return (
    <animated.div style={animatedProps}>
      {location ? <p>{location}</p> : null}
      <H1Title className="title" style={{ marginBottom: 0, paddingBottom: 0 }} dangerouslySetInnerHTML={{ __html: description }} />
    </animated.div>
  );
};

const H1Title = styled((props) => <H1 {...props} />)`
  &.title {
  }

  @media all and (max-width: 600px) {
    font-size: 29px;
  }

  &.property {
    @media all and (max-width: 600px) {
      font-size: 25px;
    }

    @media all and (max-width: 450px) {
      font-size: 22px;
    }
  }
`;

const Slide = styled.div`
  transition: opacity 0.5s ease-in-out;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: absolute;
  width: 100%;
  opacity: 0;

  &.show-slide {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border: none;
  }
`;

const HeaderTopOverlay = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.4);
  content: '';
  width: 100%;
  height: 600px;
  top: 0;

  @media all and (max-width: 600px) {
    height: 300px;
  }
`;

export const StyledHeaderTop = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 600px;
  margin: 0 auto;

  @media all and (max-width: 600px) {
    height: 300px;
  }
`;

export const StyledHeaderImage = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  background: url('https://images.unsplash.com/photo-1610304305062-1099c5e0a514?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1189&q=80')
    no-repeat;
  background-size: cover;
  background-position: center;
  padding: 0 30px;
`;

export const StyledHeaderImageServices = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  background: url('https://images.unsplash.com/photo-1556740714-a8395b3bf30f?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80')
    no-repeat;
  background-size: cover;
  background-position: center;
  padding: 0 30px;
`;

export const StyledHeaderOurCustomer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80vh;
  margin: 0 auto;
  background: url('https://images.unsplash.com/uploads/14123892966835548e7bd/14369636?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80')
    no-repeat;
  background-size: cover;
  background-position: center;
  padding: 0 30px;
`;

const StyledHeaderContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  text-align: center;
  padding: 0 50px;
  margin-top: -5em;
  p {
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.white};
  }

  @media all and (max-width: 600px) {
    padding: 0 70px;
    margin-top: 0;
  }
`;

export const PropertyUrl = styled.a`
  text-decoration: none;
`;
